import { createContext } from 'react';
import { Breakpoint } from '@mui/material';
import { IAuthUserInfo } from '@/types/Auth.ts';
import { IAppMenuitem } from '@/types/menu';
import { IModuleInfo } from '@/types/module';
import { ModuleUtils } from '@/utils/ModuleUtils.ts';
import { AppUtils } from '@/utils/AppUtils.ts';
import { IRole } from '@/types/role';
import { IUser } from '@/types/user';
import { IRolePermission } from '@/types/role/permissions.ts';
import { INotification } from '@/types/notification';

export type AppDevice = 'mobile' | 'tablet' | 'pc';
export type AppMenuWidth = 330 | 80;
export type NotificationSetType = 'set' | 'update' | 'remove';
export type WsConnectionStatus =
    | 'awaiting'
    | 'connected'
    | 'connecting'
    | 'error'
    | 'closed';

export interface IAppContextState {
    auth: IAuthUserInfo;
    layout: {
        breakpoint: Breakpoint;
        device: AppDevice;
    };
    menu: {
        open: boolean;
        width: AppMenuWidth;
        items: IAppMenuitem[];
    };
    loaded: boolean;
    lastWsNotification: INotification | null;
    notifications: INotification[];
    setAuthUserInfo: (value: IAuthUserInfo) => void;
    setNotifications: (
        value: INotification[],
        setType: NotificationSetType,
    ) => void;
    pushNotification: (value: INotification) => void;
    wsClient: WebSocket | null;
    setAuthUserRoles: (value: IRole[]) => void;
    setAuthUser: (value: IUser) => void;
    setLoaded: (value: boolean) => void;
    setMenuWidth: (value: AppMenuWidth) => void;
    setMenuOpen: (value: boolean) => void;
    cleanState: () => void;
    setWsClient: (value: WebSocket | null) => void;
    newTask: boolean;
    activeTasks: number;
    setNewTask: (value: boolean) => void;
    setActiveTask: (value: number) => void;
}

export const INITIAL_STATE: IAppContextState = {
    auth: {
        user: null,
        roles: new Array<IRole>(),
        permissions: new Array<IRolePermission>(),
    },
    layout: {
        breakpoint: 'lg',
        device: 'pc',
    },
    menu: {
        open: true,
        width: 330,
        items: [
            // {
            //     id: 'home',
            //     ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
            //         ModuleUtils.getModuleInfo('home'),
            //         ['label', 'path']
            //     ),
            //     icon: 'home',
            // },
            {
                id: 'roles',
                ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                    ModuleUtils.getModuleInfo('roles'),
                    ['label', 'path'],
                ),
                icon: 'engineering',
            },

            {
                id: 'users-options',
                label: 'Opciones de Usuarios',
                icon: 'users-options',
                subMenu: [
                    {
                        id: 'users',
                        ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                            ModuleUtils.getModuleInfo('users'),
                            ['label', 'path'],
                        ),
                        icon: 'person',
                    },
                    {
                        id: 'users-tracking',
                        //id: 'tools', //solo test
                        ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                            ModuleUtils.getModuleInfo('users-tracking'),
                            ['label', 'path'],
                        ),
                        icon: 'users-tracking',
                    },
                ],
            },
            // {
            //     id: 'bots',
            //     ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
            //         ModuleUtils.getModuleInfo('bots'),
            //         ['label', 'path']
            //     ),
            //     icon: 'adb',
            // },
            {
                id: 'groups',
                ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                    ModuleUtils.getModuleInfo('groups'),
                    ['label', 'path'],
                ),
                icon: 'people',
            },

            {
                id: 'companies',
                ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                    ModuleUtils.getModuleInfo('companies'),
                    ['label', 'path'],
                ),
                icon: 'business',
            },

            {
                id: 'tickets',
                ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                    ModuleUtils.getModuleInfo('tickets'),
                    ['label', 'path'],
                ),
                icon: 'bookmarks',
            },
            {
                id: 'contacts',
                ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                    ModuleUtils.getModuleInfo('contacts'),
                    ['label', 'path'],
                ),
                icon: 'contacts',
            },
            {
                id: 'channels',
                ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                    ModuleUtils.getModuleInfo('channels'),
                    ['label', 'path'],
                ),
                icon: 'comment_bank',
            },
            {
                id: 'reports',
                ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                    ModuleUtils.getModuleInfo('reports'),
                    ['label', 'path'],
                ),
                icon: 'description',
            },
            {
                id: 'aiSettings',
                label: 'Configuración IA',
                icon: 'aiSettings',
                subMenu: [
                    {
                        id: 'tools',
                        ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                            ModuleUtils.getModuleInfo('tools'),
                            ['label', 'path'],
                        ),
                        icon: 'build',
                    },
                    {
                        id: 'records',
                        ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
                            ModuleUtils.getModuleInfo('records'),
                            ['label', 'path'],
                        ),
                        icon: 'records',
                    },
                ],
            },
            // {
            //     id: 'settings',
            //     label: 'Configuración',
            //     icon: 'setting',
            //     subMenu: [
            //         {
            //             id: 'customFields',
            //             ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
            //                 ModuleUtils.getModuleInfo('customFields'),
            //                 ['label', 'path'],
            //             ),
            //             icon: 'note',
            //         },
            //         {
            //             id: 'tags',
            //             ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
            //                 ModuleUtils.getModuleInfo('tags'),
            //                 ['label', 'path'],
            //             ),
            //             icon: 'local_offer',
            //         },
            //
            //         {
            //             id: 'types',
            //             ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
            //                 ModuleUtils.getModuleInfo('types'),
            //                 ['label', 'path'],
            //             ),
            //             icon: 'description',
            //         },
            //         {
            //             id: 'templates',
            //             ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
            //                 ModuleUtils.getModuleInfo('templates'),
            //                 ['label', 'path'],
            //             ),
            //             icon: 'home',
            //         },
            //     ],
            // },
            // {
            //     id: 'customFields',
            //     ...AppUtils.pickObject<IModuleInfo, 'label' | 'path'>(
            //         ModuleUtils.getModuleInfo('customFields'),
            //         ['label', 'path']
            //     ),
            //     icon: 'note',
            // },
        ],
    },
    loaded: false,
    wsClient: null,
    lastWsNotification: null,
    notifications: new Array<INotification>(),
    newTask: false,
    activeTasks: 0
} as IAppContextState;

export const AppContext = createContext(INITIAL_STATE);
